import { FC, PropsWithChildren } from 'react'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { MarketingDrawer, Section } from '@app/components/general'
import { metaSetter } from '@app/utils'
import { FeaturesList } from '@app/components/stateless/marketing/features'
import { Header } from '@app/components/general/header'
import { MarketingCli } from '@app/components/stateless/marketing/cli-usage'
import { NewsSVG } from '@app/components/svgs/marketing/news'
import { ParkSvg } from '@app/components/svgs/marketing/park'
import { HealSvg } from '@app/components/svgs/marketing/heal'
import { Paragraph } from '@app/components/stateless/typo/paragraph'
import { DivContainer } from '@app/components/stateless/containers/div-container'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'
import { FILTERED_MARKETING } from '@app/configs/app-config'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/features',
    }),
  }
}

const FeatureHeading: FC<PropsWithChildren> = ({ children }) => (
  <Paragraph className='leading-7'>{children}</Paragraph>
)

// style the heading for features
const FeatureTitle: FC<PropsWithChildren> = ({ children }) => (
  <h3 className='font-bold py-2'>{children}</h3>
)

const paperStyle = 'flex place-content-center py-2 rounded'

function Features() {
  const { t, lang } = useTranslation('features')

  return (
    <MarketingDrawer>
      <DivContainer container block>
        <div className='pb-8'>
          <Header>{t('header')}</Header>
          <Paragraph>{t('header-sub')}</Paragraph>
        </div>

        <div
          className={
            'py-2 px-2 grid gap-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 rounded border-[#2A2A2A] border-t-[4px] border-2'
          }
        >
          <Section>
            <div>
              <h2 className='font-bold py-2'>{t('report-title')}</h2>
              <FeatureHeading>{t('report')}</FeatureHeading>
            </div>
            <div className={paperStyle}>
              <NewsSVG height={175} width={175} />
            </div>
          </Section>

          <Section>
            <div>
              <FeatureTitle>{t('playground-title')}</FeatureTitle>
              <FeatureHeading>{t('playground')}</FeatureHeading>
            </div>
            <div className={paperStyle}>
              <ParkSvg height={175} width={175} />
            </div>
          </Section>

          <Section>
            <div>
              <FeatureTitle>{t('api-title')}</FeatureTitle>
              <FeatureHeading>{t('api-details')}</FeatureHeading>
            </div>
            <div className={paperStyle}>
              <HealSvg height={175} width={175} />
            </div>
          </Section>
        </div>

        <FeaturesList alternative all t={t} lang={lang} />
        {FILTERED_MARKETING ? null : (
          <Section>
            <MarketingCli />
          </Section>
        )}
      </DivContainer>
    </MarketingDrawer>
  )
}

export default metaSetter(
  { Features },
  {
    translation: 'features',
  }
)
